import { arrayBufferToBase64 } from '@rsos/debug-panel/src/utils/audioFileUtils';
import { waitForWebSocketConnection } from '@rsos/debug-panel/src/utils/websocketUtils';
import { getTokens } from '@rsos/sinatra';
import { DebugPanelConfig } from './config';
import {
  AudioStreamMessage,
  RTTHeaders,
  SpeakerRole,
  AudioChunk,
} from './types';

/* eslint-disable no-console */

const DISPATCHER_CHANNEL = 0;
const CALLER_CHANNEL = 1;

export class AudioStreamer extends EventTarget {
  private mediaRecorder: MediaRecorder | null = null;
  private websocket: WebSocket | null = null;
  private audioChunks: AudioChunk[] = [];
  private callId: string;
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private channelMerger: ChannelMergerNode | null = null;
  private callerChannelGain: GainNode | null = null;
  private dispatcherChannelGain: GainNode | null = null;
  private micSource: MediaStreamAudioSourceNode | null = null;
  private config: DebugPanelConfig;
  private instanceId: string;
  private currentSpeakerRole: SpeakerRole | null = null;
  private onMessage?: (message: unknown) => void;
  private onError?: (error: string) => void;
  private collectRecordings = false;
  private recordedAudioChunks: AudioChunk[] = [];

  constructor(config: DebugPanelConfig) {
    super();
    this.config = config;
    this.callId = this.generateCallId();
    this.instanceId = this.generateCallId();
    console.debug(`AudioStreamer instance created: ${this.instanceId}`);
  }

  private generateCallId(): string {
    return Array.from(crypto.getRandomValues(new Uint8Array(16)), byte =>
      byte.toString(16).padStart(2, '0'),
    ).join('');
  }

  private async getAuthToken(): Promise<string> {
    // NOTE: the `isLocal` short-circuit (return '') is skipped on purpose.
    // It assumes localhost/devbox means auth is disabled, but a local dev-proxy
    // can front a remote devbox whose internal_auth still enforces the JWT —
    // sending no token there fails with "Failed to decode JWT: Not enough segments".
    // Sending a valid token is harmless against a truly-local backend.

    // Use the same token that Emergency Data uses for all API calls
    const { token } = getTokens() ?? { token: null };
    if (!token) {
      throw new Error(
        'No authentication token available. Please ensure you are logged in.',
      );
    }

    return token;
  }

  private createWebSocketHeaders(
    callerNumber: string,
    speakerRole: SpeakerRole,
    audioEncoding: string,
  ): RTTHeaders {
    const headers: RTTHeaders = {
      'Caller-Number': callerNumber,
      'Call-ID': this.callId,
      'Account-ID': this.config.accountId,
      'Call-Start-Time': Date.now().toString(),
      'Dispatcher-Position': speakerRole === 'dispatcher' ? '007' : '99',
      'Audio-Encoding': audioEncoding,
      'Audio-Sample-Rate': '48000',
      'Num-Audio-Channels': '2',
    };

    return headers;
  }

  async startRecording(
    callerNumber: string,
    speakerRole: SpeakerRole,
  ): Promise<void> {
    try {
      console.info(`STARTING RECORDING on instance ${this.instanceId}...`);

      this.currentSpeakerRole = speakerRole;
      this.callId = this.generateCallId();
      this.audioChunks = [];
      this.recordedAudioChunks = [];

      // Create audio context for multi-channel processing
      this.audioContext = new AudioContext({ sampleRate: 48000 });

      // Get microphone access
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 48000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Create Web Audio API nodes for multi-channel processing
      // We only need ONE source node since we're using the same microphone
      this.micSource = this.audioContext.createMediaStreamSource(
        this.mediaStream,
      );

      // Create gain nodes to control each speaker's channel
      this.callerChannelGain = this.audioContext.createGain();
      this.dispatcherChannelGain = this.audioContext.createGain();
      this.setupSpeakerGains();

      // connect microphone to both channels via the gain node
      this.micSource.connect(this.callerChannelGain);
      this.micSource.connect(this.dispatcherChannelGain);

      // use channel merger to combine 2 sources into a multi-channel stream (this is what rtt_ingress prefers)
      this.channelMerger = this.audioContext.createChannelMerger(2);

      // Connect gain nodes to specific channels of the merger
      this.callerChannelGain.connect(this.channelMerger, 0, CALLER_CHANNEL);
      this.dispatcherChannelGain.connect(
        this.channelMerger,
        0,
        DISPATCHER_CHANNEL,
      );

      this.setupSpeakerGains();

      // Create destination for the merged stream
      const destination = this.audioContext.createMediaStreamDestination();
      this.channelMerger.connect(destination);

      console.debug(
        'Audio routing: Microphone connected to both stereo channels',
      );
      console.debug(
        'Destination stream tracks:',
        destination.stream.getAudioTracks().length,
      );
      console.debug(
        'Destination stream track settings:',
        destination.stream.getAudioTracks()[0]?.getSettings(),
      );

      let options: MediaRecorderOptions;

      if (MediaRecorder.isTypeSupported('audio/webm; codecs=opus')) {
        options = {
          mimeType: 'audio/webm; codecs=opus',
          audioBitsPerSecond: 128000, // Ensure enough bandwidth for stereo
        };
        console.debug('Using WebM/Opus for stereo recording');
      } else {
        // NOTE: we can probably support others as long as deepgram does
        throw new Error('No supported codec found for stereo recording');
      }

      this.mediaRecorder = new MediaRecorder(destination.stream, options);

      // Log the actual recorder settings
      console.debug('MediaRecorder created with:', {
        mimeType: this.mediaRecorder.mimeType,
        state: this.mediaRecorder.state,
        stream: {
          id: destination.stream.id,
          tracks: destination.stream.getAudioTracks().map(track => ({
            label: track.label,
            kind: track.kind,
            settings: track.getSettings(),
          })),
        },
      });

      // Setup WebSocket connection
      await this.connectWebSocket(
        callerNumber,
        speakerRole,
        // full mimetype from chromium is "audio/webm; codecs=opus". I tried
        // opus, webm, audio/webm, etc. and none will make deepgram happy. It
        // just won't transcribe. Passing nothing into deepgram works fine.
        '',
      );

      this.setupRecorderHandlers();

      // Start the single MediaRecorder with small chunks (100ms intervals)
      const chunkIntervalMs = 100;
      this.mediaRecorder.start(chunkIntervalMs);

      this.dispatchEvent(new CustomEvent('stateChange'));
    } catch (error) {
      this.onError?.(`Failed to start recording: ${error}`);
      throw error;
    }
  }

  private async connectWebSocket(
    callerNumber: string,
    speakerRole: SpeakerRole,
    audioEncoding: string,
  ): Promise<void> {
    const token = await this.getAuthToken();
    const headers = this.createWebSocketHeaders(
      callerNumber,
      speakerRole,
      audioEncoding,
    );

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Convert HTTP/HTTPS to WS/WSS for WebSocket connection
    // This works through nginx proxy for local devbox and should work in production
    const baseWsUrl = this.config.baseUrl
      .replace('https://', 'wss://')
      .replace('http://', 'ws://');
    const wsUrl = `${baseWsUrl}/v1/rtt_ingress/browser-ws`;

    const url = new URL(wsUrl);
    Object.entries(headers).forEach(([key, value]) => {
      if (value) url.searchParams.set(key, value);
    });

    try {
      this.websocket = new WebSocket(url.toString());
    } catch (error) {
      this.onError?.(`WebSocket instantiation error: ${error}`);
      throw error;
    }

    // Wait for connection to open or fail
    try {
      await waitForWebSocketConnection(this.websocket);
      this.dispatchEvent(new CustomEvent('stateChange'));
    } catch (error) {
      this.onError?.(`WebSocket connection error: ${error}`);
      throw error;
    }

    // attach event handlers
    this.websocket.onmessage = event => {
      try {
        const message = JSON.parse(event.data);
        console.debug(
          `Received WebSocket message on instance ${this.instanceId}:`,
          message,
        );
        this.onMessage?.(message);
        if (message.error) {
          this.onError?.(`WebSocket error: ${JSON.stringify(message.error)}`);
        }
      } catch (error) {
        this.onError?.(`Failed to parse WebSocket message: ${error}`);
      }
    };

    this.websocket.onclose = () => {
      this.dispatchEvent(new CustomEvent('stateChange'));
    };
  }

  private setupSpeakerGains(): void {
    if (
      !this.callerChannelGain ||
      !this.dispatcherChannelGain ||
      !this.currentSpeakerRole
    ) {
      throw new Error(
        'Cannot setup speaker gains - gain nodes or speaker role not available',
      );
    }

    if (this.currentSpeakerRole === 'caller') {
      this.callerChannelGain.gain.value = 1.0;
      this.dispatcherChannelGain.gain.value = 0.0;
      console.debug('Speaker gains set for caller');
    } else {
      this.callerChannelGain.gain.value = 0.0;
      this.dispatcherChannelGain.gain.value = 1.0;
      console.debug('Speaker gains set for dispatcher');
    }
  }

  private setupRecorderHandlers(): void {
    if (!this.mediaRecorder) return;

    this.mediaRecorder.ondataavailable = async (event: BlobEvent) => {
      if (event.data.size > 0) {
        const chunk: AudioChunk = {
          data: await event.data.arrayBuffer(),
          timestamp: Date.now(),
        };
        this.audioChunks.push(chunk);

        if (this.collectRecordings) {
          this.recordedAudioChunks.push(chunk);
          this.dispatchEvent(new CustomEvent('stateChange'));
        }

        await this.sendAudioChunk(chunk);
      }
    };

    this.mediaRecorder.onerror = (error: Event) => {
      const errorMessage =
        error instanceof ErrorEvent
          ? error.message
          : `MediaRecorder error - ${error.toString()}`;
      this.onError?.(`MediaRecorder error: ${errorMessage}`);
    };
  }

  private async sendAudioChunk(chunk: AudioChunk): Promise<void> {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
      return;
    }

    try {
      const base64 = arrayBufferToBase64(chunk.data);
      const message: AudioStreamMessage = {
        audio_data: base64,
        call_status:
          this.audioChunks.length === 1 ? 'call_start' : 'call_in_progress',
      };

      this.websocket.send(JSON.stringify(message));
    } catch (error) {
      this.onError?.(`Failed to send audio chunk: ${error}`);
    }
  }

  async stopRecording(): Promise<void> {
    console.debug(`STOPPING RECORDING on instance ${this.instanceId}...`);

    if (this.mediaRecorder) {
      try {
        this.mediaRecorder.stop();
      } catch (error) {
        console.error('Error stopping MediaRecorder:', error);
      }
      this.mediaRecorder.ondataavailable = null;
      this.mediaRecorder.onerror = null;
      this.mediaRecorder = null;
    }

    if (this.micSource) {
      this.micSource.disconnect();
      this.micSource = null;
    }

    if (this.channelMerger) {
      this.channelMerger.disconnect();
      this.channelMerger = null;
    }

    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }

    if (this.mediaStream) {
      console.debug('Stopping MediaStream tracks...');
      for (const track of this.mediaStream.getTracks()) {
        console.debug('Stopping track:', track);
        track.stop();
      }
      this.mediaStream = null;
    } else {
      console.debug('No active MediaStream to stop.');
    }

    if (this.websocket) {
      console.debug('Closing WebSocket connection...');
      if (this.websocket.readyState === WebSocket.OPEN) {
        console.debug("Sending 'call_ended' message...");
        const message: AudioStreamMessage = {
          audio_data: '',
          call_status: 'call_ended',
        };
        this.websocket.send(JSON.stringify(message));
      }
      console.debug('Closing WebSocket...');
      this.websocket.close();
      this.websocket = null;
    } else {
      console.debug('No active WebSocket to close.');
    }

    // Clean up state
    this.audioChunks = [];
    this.currentSpeakerRole = null;
    this.callId = this.generateCallId();
    this.recordedAudioChunks = [];
    this.dispatchEvent(new CustomEvent('stateChange'));
  }

  setMessageHandler(handler: (message: unknown) => void): void {
    this.onMessage = handler;
  }

  setErrorHandler(handler: (error: string) => void): void {
    this.onError = handler;
  }

  isRecording(): boolean {
    return this.mediaRecorder?.state === 'recording';
  }

  isConnected(): boolean {
    return this.websocket?.readyState === WebSocket.OPEN;
  }

  setSpeakerRole(speakerRole: SpeakerRole): void {
    this.currentSpeakerRole = speakerRole;
    this.setupSpeakerGains();
  }

  setCollectRecordings(collect: boolean): void {
    this.collectRecordings = collect;
    this.dispatchEvent(new CustomEvent('stateChange'));
  }

  isCollectingRecordings(): boolean {
    return this.collectRecordings;
  }

  getRecordedAudioChunks(): AudioChunk[] {
    return [...this.recordedAudioChunks]; // Return copy to prevent external mutation
  }

  getRecordedAudioChunkCount(): number {
    return this.recordedAudioChunks.length;
  }
}
